import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Stack } from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { clientIcon } from 'c-main/icons';
import type { Client } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import {
    AllEntitiesDropdown,
    ControlledFormInput,
    CreateEntityButton,
    TextField,
} from 'c-components';
import type { FilterableEntityTableRefAPI } from 'c-pagination';
import {
    FilterableEntityTable,
    FilterableEntityTableRendererType,
    generateColumn,
} from 'c-pagination';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReactGA from 'react-ga';
import ClientControlsCell from './ClientControlsCell';

const columns = [
    generateColumn<Client>({
        headerTitle: 'Modules.Main.Client.List.table.idCol',
        field: 'id',
        entityName: 'Client',
        orderable: true,
    }),
    generateColumn<Client>({
        headerTitle: 'Modules.Main.Client.List.table.nameCol',
        field: 'name',
        entityName: 'Client',
        orderable: true,
    }),
    generateColumn<Client>({
        headerTitle: 'Modules.Main.Client.List.table.marketCol',
        field: 'market.name',
        entityName: 'Client',
        filterKeyPath: 'market.id',
        permissions: [PermissionName.UniledportalMarketRead],
    }),
    generateColumn<Client>({
        headerTitle: '',
        field: 'controls',
        entityName: 'Client',
        RenderCell: ClientControlsCell,
    }),
];
const paginationTag = 'client-list-home-search';

const includes = ['market'];
const textSearchCols = ['name'];
const schema = yup.object({
    name: yup.string().required(),
    market_id: yup.number().required(),
});

const ClientListPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const ref = useRef<FilterableEntityTableRefAPI>();
    const onClientCreated = useCallback(() => {
        ref?.current?.search();
    }, []);
    const t = useCommonTranslation();
    return (
        <FullPageEntityTableWrapper
            title="Modules.Main.Client.List.listTitle"
            controls={useMemo(
                () => [
                    <CreateEntityButton<Client>
                        key="new-client-btn"
                        entityName="Client"
                        title="Modules.Main.Client.List.newClientButton"
                        buttonLabel={t('Modules.Main.Client.List.newClientButton')}
                        submitLabel="Modules.Main.Client.List.newClientSubmit"
                        form={<NewClientForm />}
                        formOptions={{
                            resolver: yupResolver(schema),
                        }}
                        permissions={[PermissionName.UniledportalClientCreate]}
                        onSuccess={onClientCreated}
                    />,
                ],
                [onClientCreated, t],
            )}
        >
            <FilterableEntityTable
                ref={ref}
                key={paginationTag}
                tag={paginationTag}
                columns={columns}
                baseEntityName="Client"
                includes={includes}
                showFilters
                textSearchColumns={textSearchCols}
                rendererType={FilterableEntityTableRendererType.Contained}
            />
        </FullPageEntityTableWrapper>
    );
};

const NewClientForm = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const t = useCommonTranslation();
    return (
        <Stack gap={2}>
            <ControlledFormInput
                render={({ field }) => (
                    <TextField
                        label={t('Modules.Main.Client.List.update.nameLabel')}
                        {...field}
                        autoFocus
                    />
                )}
                name="name"
            />
            <ControlledFormInput
                render={({ field }) => (
                    <AllEntitiesDropdown
                        entityName="Market"
                        labelField="name"
                        label={t('Modules.Main.Client.List.update.marketLabel')}
                        {...field}
                    />
                )}
                name="market_id"
            />
        </Stack>
    );
};

const setup: PageConfig = {
    id: 'ClientListPage',
    component: ClientListPage,
    disableDesktopContentScrolling: true,
    systemSearch: {
        title: 'Modules.Main.Client.List.metaTitle',
        description: 'Modules.Main.Client.List.description',
        type: 'page',
        icon: clientIcon,
        aliases: 'SystemSearch.aliases.clientListPage',
        route: PostAuthRoutes.Client.ClientList,
        saveAsRecentPage: true,
        breadcrumbs: [],
        permissions: [
            PermissionName.UniledportalClientRead,
            PermissionName.UniledportalClientCreate,
            PermissionName.UniledportalClientUpdate,
        ],
    },
};

export default setup;
